// Talking to the league server (/api/leagues), and the league tokens this browser holds.
// A token says which player you are in a league; it stays in localStorage, like your name.

export type LeagueStatus = 'open' | 'building' | 'playing' | 'done';

export interface LeagueSettings {
  game: string;
  format: 'single' | 'double';
  seats: number;
  choices: 2 | 3;
  armies?: 'builder' | 'fixed' | 'pick';   // the army builder, the same built-in team for all, or each picks one
  team?: string;                           // the built-in team for 'fixed' (a key of the game's TEAMS)
  voidAfterDays: number | null;
  abilities: boolean;
  improve: boolean;
  gameOptions?: Record<string, string | number | boolean>;   // Conquer the Flag options for every game (e.g. prisons)
}

export interface ArmyUnit { role: string; type: string; nick: string }
export interface Army { name: string; code?: string; units?: ArmyUnit[]; improvements?: { unit: number; choice: string; at?: number; round?: number }[]; builtBy?: string }

export interface LeaguePlayer {
  id: string;
  name: string;
  bot: boolean;
  joinedAt: number;
  armyBuilt: boolean;
  pending: number;       // improvements earned and not chosen yet
  away: boolean;         // a bot is playing this person's games for now (the host can give the seat back)
  army: Army | null;     // the full army once every army is built (your own, or a bot's for the host, sooner)
}

export interface LeagueGame {
  id: string;            // "r2g1"
  blue: string;          // player ids
  red: string;
  result: { winner: string | null; how: string | null; at: number; by: string } | null;
  voided: boolean;
  autoVoided?: boolean;
  hasReplay?: boolean;
}

export interface LeagueView {
  id: string;
  name: string;
  status: LeagueStatus;
  settings: LeagueSettings;
  seed: number;
  hostId: string;
  createdAt: number;
  startedAt: number | null;
  players: LeaguePlayer[];
  rounds: { games: LeagueGame[] }[];
  current: number;
  standings: { id: string; w: number; l: number; d: number }[];
  me: { id: string; isHost: boolean; recovery: string; away: boolean } | null;
  live: Record<string, boolean>;
  botsPlaying?: Record<string, boolean>;   // games between two bots the server is playing now
  storage: 'database' | 'memory';
}

const KEY = 'league-tokens';

interface Held { token: string; name: string; at: number }
function held(): Record<string, Held> {
  try { const v = JSON.parse(localStorage.getItem(KEY) ?? '{}'); return v && typeof v === 'object' ? v : {}; } catch { return {}; }
}
export function leagueToken(id: string): string | null { return held()[id]?.token ?? null; }
export function rememberLeague(id: string, token: string, name: string) {
  const all = held();
  all[id] = { token, name, at: Date.now() };
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch { /* storage blocked */ }
}
export function touchLeague(id: string, name: string) {
  const all = held();
  if (!all[id]) return;
  all[id] = { ...all[id], name, at: Date.now() };
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch { /* storage blocked */ }
}
export function forgetLeague(id: string) {
  const all = held();
  delete all[id];
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch { /* storage blocked */ }
}
// Leagues this browser is in, newest first (for the landing page).
export function heldLeagues(): { id: string; name: string; at: number }[] {
  return Object.entries(held()).map(([id, h]) => ({ id, name: h.name, at: h.at })).sort((a, b) => b.at - a.at);
}

async function call<T>(path: string, init: RequestInit = {}, token: string | null = null): Promise<T> {
  const r = await fetch(`/api/leagues${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(token ? { 'x-league-token': token } : {}), ...(init.headers ?? {}) },
  });
  let body: { error?: string } & Record<string, unknown> = {};
  try { body = await r.json(); } catch { /* not JSON */ }
  if (!r.ok) throw new Error(body.error ?? `The server said ${r.status}.`);
  return body as T;
}

export const getLeague = (id: string) =>
  call<{ league: LeagueView }>(`/${encodeURIComponent(id)}`, {}, leagueToken(id)).then(r => r.league);

export async function createLeague(name: string, hostName: string, settings: Partial<LeagueSettings>): Promise<LeagueView> {
  const r = await call<{ league: LeagueView; token: string }>('', { method: 'POST', body: JSON.stringify({ name, hostName, settings }) });
  rememberLeague(r.league.id, r.token, r.league.name);
  return r.league;
}

// Any change: join, recover, bot, remove, start, army, result, void.
export async function act(id: string, action: string, body: Record<string, unknown> = {}): Promise<LeagueView> {
  const r = await call<{ league: LeagueView; token?: string }>(`/${encodeURIComponent(id)}/${action}`, { method: 'POST', body: JSON.stringify(body) }, leagueToken(id));
  if (r.token) rememberLeague(id, r.token, r.league.name);
  return r.league;
}

// A league game's replay, for the replay page.
export const getReplay = (id: string, gameId: string) =>
  call<{ replay: string; blue: string; red: string; result: LeagueGame['result']; league: string }>(`/${encodeURIComponent(id)}/replay/${encodeURIComponent(gameId)}`);

export const playerName = (L: LeagueView, id: string | null | undefined) => L.players.find(p => p.id === id)?.name ?? '?';
export const gameRoomSlug = (leagueId: string, gameId: string) => `lg-${leagueId}-${gameId}`;
